import React, { useEffect, useState } from "react";
import { referralsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Users, Clock, Trophy, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [showBoard, setShowBoard] = useState(false);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    referralsApi.status().then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    if (showBoard && !board) {
      referralsApi.leaderboard(10).then(setBoard).catch(() => setBoard({ leaderboard: [] }));
    }
  }, [showBoard, board]);

  if (!data) return null;

  const shareUrl = `${window.location.origin}/?ref=${encodeURIComponent(data.referral_code)}`;
  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const feeFreeLeft = data.fee_free_active && data.fee_free_until
    ? Math.max(0, Math.ceil((new Date(data.fee_free_until) - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div data-testid="referral-card" className={`aq-card p-4 ${data.fee_free_active ? "border-emerald-500/40 bg-emerald-500/5" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className={`w-4 h-4 ${data.fee_free_active ? "text-emerald-400" : "text-indigo-400"}`} />
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Refer & win</div>
        </div>
        {data.fee_free_active && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
            <Clock className="w-3 h-3 inline mr-1" /> {feeFreeLeft}d fee-free
          </span>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400 leading-relaxed">
        Share your link. Every friend who closes their first profitable trade earns
        <b className="text-emerald-300"> both of you a {data.fee_free_reward_days}-day fee-free week</b> (waived 1.25% on all profits — bring-a-friend match).
      </p>

      <div className="mt-3 rounded-md bg-black/40 border border-white/5 p-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Your code</div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <div data-testid="referral-code" className="font-mono font-bold text-emerald-300 truncate">{data.referral_code}</div>
          <Button
            data-testid="referral-copy-code"
            onClick={() => copy(data.referral_code)}
            variant="ghost"
            className="h-7 px-2 text-slate-400 hover:text-white hover:bg-white/10"
          >
            <Copy className="w-3 h-3" />
          </Button>
        </div>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Share link</div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <div data-testid="referral-link" className="font-mono text-[11px] text-slate-300 truncate">{shareUrl}</div>
          <Button
            data-testid="referral-copy-link"
            onClick={() => copy(shareUrl)}
            variant="ghost"
            className="h-7 px-2 text-slate-400 hover:text-white hover:bg-white/10"
          >
            <Copy className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Stat icon={<Users className="w-3.5 h-3.5"/>} label="Joined" value={data.referrals_joined} />
        <Stat icon={<Gift className="w-3.5 h-3.5"/>} label="Converted" value={data.referrals_converted} />
      </div>

      <button
        data-testid="referral-leaderboard-toggle"
        onClick={() => setShowBoard((v) => !v)}
        className="mt-3 w-full flex items-center justify-between text-xs px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 transition"
      >
        <span className="flex items-center gap-2 text-slate-300">
          <Trophy className="w-3.5 h-3.5 text-amber-300" /> Top referrers
        </span>
        {showBoard ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
      </button>
      <a
        data-testid="referral-full-leaderboard-link"
        href="/leaderboard"
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-300 hover:text-emerald-200 py-1.5"
      >
        Open full leaderboard →
      </a>
      {showBoard && (
        <div data-testid="referral-leaderboard" className="mt-2 space-y-1.5 max-h-52 overflow-y-auto aq-scroll">
          {(!board || !board.leaderboard) && <div className="text-[11px] text-slate-500 font-mono">Loading…</div>}
          {board && board.leaderboard?.length === 0 && (
            <div className="text-[11px] text-slate-500 font-mono">No referrals yet — be the first.</div>
          )}
          {board?.leaderboard?.map((row, i) => (
            <div
              key={row.referral_code}
              className={`flex items-center justify-between px-2 py-1.5 rounded-md text-xs ${row.referral_code === data.referral_code ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-white/5"}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`font-mono text-[10px] w-5 text-center ${i < 3 ? "text-amber-300" : "text-slate-500"}`}>#{i + 1}</span>
                <span className="truncate text-slate-200">{row.display_name}</span>
                {row.referral_code === data.referral_code && (
                  <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">you</span>
                )}
              </div>
              <div className="font-mono text-slate-300 shrink-0">
                {row.referrals_converted}<span className="text-slate-500">/{row.referrals_joined}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-md bg-white/5 border border-white/5 p-2 flex items-center justify-between">
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1">
        {icon} {label}
      </span>
      <span className="font-mono font-bold text-slate-200">{value}</span>
    </div>
  );
}
